import {
  format as formatFns,
  parse,
  parseISO,
  getYear,
  getMonth,
  differenceInDays,
  differenceInCalendarDays,
  differenceInHours,
  differenceInSeconds,
  addMonths as addMonthsFns,
  addDays as addDaysFns,
  addSeconds as addSecondsFns,
  addHours as addHoursFns,
  isValid as isValidFns,
  type FormatOptions,
} from 'date-fns'
import { enUS, es, fr, de, it, ptBR, ja, type Locale } from 'date-fns/locale'
import _isNil from 'lodash/isNil'

const localeMap = {
  en: enUS,
  es,
  fr,
  de,
  it,
  pt: ptBR,
  ja,
}

function getUserLocale(): Locale {
  // Prefer the document <html lang> to keep SSR/CSR deterministic.
  let langCode: string | undefined

  if (typeof document !== 'undefined') {
    const htmlLang = document.documentElement?.lang
    if (htmlLang) {
      langCode = htmlLang
    }
  }

  // Fallback to navigator on the client only
  if (!langCode && typeof navigator !== 'undefined' && navigator.language) {
    langCode = navigator.language
  }

  // Final fallback for SSR or unknown cases
  if (!langCode) {
    langCode = 'en-US'
  }

  const short = langCode.split('-')[0]
  return localeMap[short as keyof typeof localeMap] || enUS
}

class DateUtils {
  static async sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  static now(): Date {
    return new Date()
  }

  static nowString(): string {
    return this.now().toISOString()
  }

  static nowDay(): number {
    return new Date().getDate()
  }

  static nowYear(): number {
    return new Date().getFullYear()
  }

  static format(
    date: Date,
    format: string,
    { options = {} }: { options?: FormatOptions } = {}
  ): string {
    if (_isNil(date)) {
      return date
    }

    return formatFns(date, format, options)
  }

  static formatLocalDate(date: Date | string | null): string {
    if (!date) {
      return ''
    }

    const options = { locale: getUserLocale() }
    return this.format(this.parse(date), 'PP', { options })
  }

  static formatLocalDateTime(date: Date | string | null): string {
    if (!date) {
      return ''
    }

    const options = { locale: getUserLocale() }
    return this.format(this.parse(date), 'PP p', { options })
  }

  static parse(
    dateString: string | Date,
    { format, referenceDate = new Date() }: { format?: string; referenceDate?: Date } = {}
  ): Date {
    if (dateString instanceof Date) {
      return dateString
    }
    if (_isNil(dateString)) {
      return dateString
    }
    if (!format) {
      return new Date(dateString)
    }
    return parse(dateString, format, referenceDate)
  }

  static parseIso(dateString: string): Date {
    return parseISO(dateString)
  }

  static isValid(date: Date | string | null | undefined): boolean {
    if (_isNil(date)) {
      return false
    }
    return isValidFns(this.parse(date))
  }

  static mondayDateColumnToIsoUtc(value: string, text: string): string | null {
    try {
      const { date, time } = JSON.parse(value || '{}')
      if (date && time) {
        return parseISO(`${date}T${time}Z`).toISOString()
      }
      if (date) {
        // When setting Monday dates avoid dates-only columns. Prefer the date+time column.
        let dateObj = this.parse(date, { format: 'yyyy-MM-dd' })
        dateObj = this.addHours(dateObj, 23)
        return dateObj.toISOString()
      }
    } catch {
      /* fall through */
    }
    const datePart = text.trim().split(' ')[0]
    if (!datePart) {
      return null
    }
    return this.toDateIso(this.parse(datePart, { format: 'yyyy-MM-dd' }))
  }

  static toDateIso(date: Date | string): string {
    if (_isNil(date)) {
      return date
    }

    const parsed = this.parse(date)
    return parsed.toISOString()
  }

  static getYear(date: Date): number {
    if (date.getFullYear) {
      return date.getFullYear()
    }
    return getYear(date)
  }

  static getMonth(date: Date): number {
    const month = date.getMonth ? date.getMonth() : getMonth(date)
    // returns the month as a number from 1 (January) to 12 (December)
    return month + 1
  }

  static getMonthDisplay(date: Date): string {
    const options = { locale: getUserLocale() }
    // returns the month name (e.g., "January", "February", etc.)
    return this.format(date, 'MMMM', { options })
  }

  static getCurrentYear() {
    const date = new Date()
    const year = date.getFullYear()
    return +year
  }

  static addMonths(date: Date | string, months: number): Date {
    return addMonthsFns(this.parse(date), months)
  }

  static addDays(date: Date | string, days: number): Date {
    return addDaysFns(this.parse(date), days)
  }

  static addHours(date: Date | string, hours: number): Date {
    return addHoursFns(this.parse(date), hours)
  }

  static addSeconds(date: Date | string, seconds: number): Date {
    return addSecondsFns(this.parse(date), seconds)
  }

  /**
   * Calculates the number of calendar days between two dates.
   *
   * A positive number means `date2` is before `date1`.
   * A negative number means `date2` is after `date1`.
   * Zero means both dates fall on the same calendar day.
   */
  static differenceInDays(date1: Date | string, date2: Date | string): number {
    return differenceInDays(this.parse(date1), this.parse(date2))
  }

  /** True when referenceDate's calendar day is after date's calendar day. */
  static isPastCalendarDate(
    date: Date | string | null | undefined,
    referenceDate: Date | string = this.now()
  ): boolean {
    if (!date) {
      return false
    }

    const reference =
      referenceDate instanceof Date ? referenceDate : this.parse(referenceDate)
    return differenceInCalendarDays(reference, this.parse(date)) > 0
  }

  static isThisOrNextMonth(
    date: Date | string | null | undefined,
    referenceDate: Date | string = this.now()
  ): boolean {
    if (!date) {
      return false
    }

    const reference =
      referenceDate instanceof Date ? referenceDate : this.parse(referenceDate)
    const end = this.parse(date)
    const endIndex = end.getFullYear() * 12 + end.getMonth()
    const currentIndex = reference.getFullYear() * 12 + reference.getMonth()

    return endIndex === currentIndex || endIndex === currentIndex + 1
  }

  static differenceInHours(date1: Date | string, date2: Date | string): number {
    return differenceInHours(this.parse(date1), this.parse(date2))
  }

  static differenceInSeconds(date1: Date | string, date2: Date | string): number {
    return differenceInSeconds(this.parse(date1), this.parse(date2))
  }
}

export default DateUtils
